// mission-workspace.ts
//
// Dựng workspace cho một mission: worktree cách ly + pack GHIM + symlink farm +
// settings mission theo profile tool §3.2 + ignore entries + entry registry managed.
//
// Ba điều KHÔNG được làm (§9.4, D06):
//  1. Không đụng checkout/HEAD của source repo (worktree init lo phần đó).
//  2. Không copy `permissions.allow` từ template — quyền worker đến từ
//     `--allowedTools` trên argv (P2); `permissions.allow` chỉ xuất hiện khi
//     cờ `permissions_allow_fallback=true` được truyền VÀO (module này không
//     tự đọc config/manifest — P5 `autopilot init` mới là nơi load).
//  3. Không tự đọc `~/.avatar/*`: mọi path đều là tham số (test tiêm tmp dir).

import { join } from "node:path";
import { ensureDir, writeJsonAtomic } from "../filesystem-helpers.js";
import { installPackFromLocalDir } from "../install-pack-from-local-dir.js";
import { syncAllMountDirs } from "../symlink-farm-for-team-pack-mount-dirs.js";
import { withSettingsFileLock } from "../with-settings-file-lock.js";
import {
  DEFAULT_REGISTRY_PATH,
  recordManagedMissionWorkspace,
} from "../workspace-registry-store.js";
import { writeDangerousCommandGuardOptInFlag } from "./dangerous-command-guard-opt-in.js";
import { initMissionWorktree, type WorktreeInitResult } from "./mission-initializer-worktree.js";
import { missionLayoutFromWorkspace } from "./mission-resolver.js";
import {
  buildMissionSettings,
  buildToolState,
  computeSettingsProfileDigest,
  type MissionSettingsInput,
} from "./mission-settings-profile.js";
import { buildMissionHooks } from "./mission-workspace-hooks.js";
import { writeWorktreeExcludes } from "./mission-worktree-excludes.js";
import { resolveSourceGitDir } from "./source-git-dir-resolver.js";
import { canonicalWorktreePath } from "./worktree-bound-tool-rules.js";

// Re-export để call site (và test) chỉ cần nhớ MỘT module cho "mission workspace".
export {
  AUTOPILOT_REQUIRED_HOOK_FILES,
  buildMissionSettings,
  computeSettingsProfileDigest,
  MISSION_TOOLS_DISABLED,
  MISSION_TOOLS_ENABLED,
  type MissionHooks,
  type MissionSettings,
} from "./mission-settings-profile.js";
export { buildMissionHooks } from "./mission-workspace-hooks.js";
// Ignore entries + `.gitignore` writer + legacy lock detection live in
// mission-worktree-excludes.ts (review 06g M1, line budget); same import path.
export {
  detectLegacySessionLockViolation,
  MISSION_IGNORE_ENTRIES,
  writeWorktreeExcludes,
} from "./mission-worktree-excludes.js";

export interface MissionWorkspaceInput extends MissionSettingsInput {
  sourceRepo: string;
  worktreePath: string;
  missionId: string;
  /** Thư mục pack nguồn để GHIM (copy) vào mission — pack bất biến suốt mission. */
  packSourceDir: string;
  registryPath?: string;
  now?: () => Date;
  /** base_sha đã validate (preflight); undefined ⇒ HEAD. */
  baseSha?: string;
}

export interface MissionWorkspaceResult {
  worktree: WorktreeInitResult;
  settingsPath: string;
  toolStatePath: string;
  /** sha256 của settings mission + profile tool đã áp (spec §3.2, N36). */
  settingsProfileSha256: string;
  mountDirs: string[];
}

export async function createMissionWorkspace(
  input: MissionWorkspaceInput,
): Promise<MissionWorkspaceResult> {
  const now = input.now ?? (() => new Date());

  // 1. Worktree cách ly (không đụng HEAD/checkout source).
  const worktree = await initMissionWorktree(
    input.sourceRepo,
    input.worktreePath,
    input.missionId,
    undefined,
    input.baseSha,
  );

  // 2. GHIM pack vào trong mission workspace (bản copy riêng, bất biến suốt mission).
  const claudeDir = join(input.worktreePath, ".claude");
  const packDir = join(claudeDir, "pack");
  await ensureDir(claudeDir);
  await installPackFromLocalDir(input.packSourceDir, packDir);

  // 3. Symlink farm 7 mount dir — dùng lại đúng hàm của đường interactive.
  const mounts = await syncAllMountDirs(packDir, claudeDir, true);

  // 4. Settings mission + state tool theo profile. `withSettingsFileLock` giữ
  // nguyên dù worktree vừa tạo (audit gate #164: MỌI writer settings.json qua
  // bracket, không miễn trừ theo ca). F4: hooks dựng ở tầng I/O này rồi truyền
  // xuống, giữ `buildMissionSettings` thuần.
  // 06g R1a (B5) + re-audit N7: the host paths the deny list is bound to —
  // the source repo's real git dir, the source checkout (its `.env`), the
  // control root and the workspace root (`.mattermost`). The caller (init)
  // passes the last two; the layout convention is the fallback.
  const layout = missionLayoutFromWorkspace(input.worktreePath);
  const settings = buildMissionSettings({
    ...input,
    sourceGitDir: await resolveSourceGitDir(input.worktreePath),
    sourceRepo: canonicalWorktreePath(input.sourceRepo),
    controlRoot: canonicalWorktreePath(input.controlRoot ?? layout.controlRoot),
    workspaceRoot: canonicalWorktreePath(input.workspaceRoot ?? layout.workspaceRoot),
    hooks: input.hooks ?? (await buildMissionHooks(packDir)),
  });
  const settingsPath = join(claudeDir, "settings.json");
  const toolState = buildToolState(now());
  const toolStatePath = join(claudeDir, "avatar-tools.json");
  await withSettingsFileLock(input.worktreePath, async () => {
    await writeJsonAtomic(settingsPath, settings, 0o600);
    await writeJsonAtomic(toolStatePath, toolState);
  });

  // 5. Ignore entries CHÍNH XÁC — ghi vào `.gitignore` TRONG worktree (xem ghi chú
  //    ở `writeWorktreeExcludes`: git chỉ đọc info/exclude của common dir).
  await writeWorktreeExcludes(input.worktreePath);

  // 5b. team-ai-pack origin/main (7a29c58): pretooluse-dangerous-command-guard-hook.py
  // is now OPT-IN — `guard_enabled()` reads this exact flag file and `exit 0`s BEFORE
  // reading stdin when it's absent, silently disabling the autopilot deny-by-binding
  // guard (P1/spec §21/F17). Path is controller-owned (already covered by
  // `.claude/state/**` in CONTROLLER_OWNED_PATH_GLOBS and by MISSION_IGNORE_ENTRIES
  // above — no ticket's `allowed_paths` should ever need to touch it).
  await writeDangerousCommandGuardOptInFlag(claudeDir);

  // 6. Digest settings + profile để P5 doctor đưa vào compatibility manifest (N36).
  const settingsProfileSha256 = computeSettingsProfileDigest(settings, toolState);

  // 7. Registry: đánh dấu workspace này là MANAGED để P9 `sync --all` bỏ qua.
  await recordManagedMissionWorkspace(
    input.worktreePath,
    input.missionId,
    input.registryPath ?? DEFAULT_REGISTRY_PATH,
    now(),
  );

  return {
    worktree,
    settingsPath,
    toolStatePath,
    settingsProfileSha256,
    mountDirs: mounts.map((m) => m.dir),
  };
}
